import React from 'react'
import { useGetBusiness, useGetBusinessID } from '@marketing-milk/frontend'
import {
  AdCreative,
  BusinessError,
  DraftCampaign,
  PublishCampaignDTO,
  SavedAudience,
} from '@marketing-milk/interfaces'
import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material'
import { campaignService } from 'app/services'
import { CampaignDetails } from 'components'
import {
  AdminFixLink,
  ADS_PUBLISH_WARNING_MESSAGE,
  ADS_PUBLISH_WARNING_TITLE,
} from 'components/BusinessErrorBanner'
import { BlankPageLayout } from 'components/BlankPageLayout'
import { Step } from 'components/Stepper/Step'
import { Stepper } from 'components/Stepper/Stepper'
import { useDraftCampaign } from 'hooks/api/useDraftCampaign'
import { useSprint } from 'hooks/api/useSprint'
import {
  getAdCreativeGroups,
  isValidTime,
} from 'pages/business/campaigns/helpers/campaignForm.helpers'
import { format, fromUnixTime } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { DateResolutionStrategy, resolveDraftDates } from './campaignDates.helpers'
import { StaleDraftDatesNotice } from './StaleDraftDatesNotice'

interface Props {
  draftCampaigns: DraftCampaign[]
  audiences?: SavedAudience[]
  creatives?: AdCreative[]
  isOpen: boolean
  sprintId: number
  onClose: () => void
}

export const PublishDraftCampaignsModal = ({ draftCampaigns, ...props }: Props) => {
  const history = useHistory()
  const businessId = useGetBusinessID()
  // Keep names only for the warning when a selected draft leaves the eligible list.
  const [draftSelection, setDraftSelection] = useState<Array<{ id: number; name: string }>>([])
  const [currentViewedDraftId, setCurrentViewedDraftId] = useState<number>()
  const [uploading, setUploading] = useState(false)
  const [uploadComplete, setUploadComplete] = useState(false)
  // One row per selected draft, seeded before publishing starts so the list keeps the order the
  // AE picked in and every campaign is accounted for while its request is still in flight.
  const [publishResults, setPublishResults] = useState<PublishResult[]>([])
  const { deleteDraftCampaign } = useDraftCampaign()
  const { refetchPublishedCampaigns } = useSprint(props.sprintId)
  const business = useGetBusiness()

  // The AE's choices are modal state only — nothing is persisted back to the draft. The modal never
  // unmounts (SprintDraftCampaigns always renders it and only toggles isOpen), so choices survive
  // closing and reopening the drawer.
  const [resolutionByDraftId, setResolutionByDraftId] = useState<
    Record<number, DateResolutionStrategy>
  >({})

  // Held in state rather than read per render so previews don't drift mid-session; refreshed each
  // time the drawer opens, so a page left open across midnight re-evaluates staleness.
  const [now, setNow] = useState(() => new Date())

  // Unlike the date resolutions above, this one is reset every time the drawer opens: the modal
  // never unmounts, and live-by-default has to hold for every publish session, not just the first.
  const [publishPaused, setPublishPaused] = useState(false)

  useEffect(() => {
    if (props.isOpen) {
      setNow(new Date())
      setPublishPaused(false)
      // The modal never unmounts, so without this the drawer reopens on the previous publish's
      // results screen — with its drafts already deleted and no way back to the stepper. Only a
      // finished publish clears the selection; closing the drawer mid-flow keeps the AE's picks.
      if (uploadComplete) {
        setUploadComplete(false)
        setPublishResults([])
        setDraftSelection([])
      }
    }
    // Keyed to opening only: a publish finishing while the drawer is open must not clear it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen])

  // Resolve IDs to current drafts so edits made while this drawer is closed reach
  // both the preview and publishing. Ineligible drafts stay out of both.
  const draftsById = new Map(draftCampaigns.map(draft => [draft.id, draft]))
  const selectedDrafts = draftSelection.flatMap(({ id }) => draftsById.get(id) ?? [])
  const withdrawnDrafts = draftSelection.filter(({ id }) => !draftsById.has(id))
  const currentViewedDraft =
    selectedDrafts.find(draft => draft.id === currentViewedDraftId) ?? selectedDrafts[0]
  const previewDates =
    currentViewedDraft &&
    resolveDraftDates(
      currentViewedDraft.config.campaign,
      resolutionByDraftId[currentViewedDraft.id],
      now,
      business.timezone
    )

  const staleSelectedDrafts = selectedDrafts.filter(
    draft => resolveDraftDates(draft.config.campaign, undefined, now, business.timezone).stale
  )
  // A choice made earlier can expire: resolutions persist across drawer opens but `now` does not,
  // so "keep the end date" picked yesterday on a draft ending yesterday is no longer a valid
  // answer today. Checking only that some choice is set would let that publish, sending Meta a
  // start time at or after the end time.
  const unresolvedStaleDrafts = staleSelectedDrafts.filter(draft => {
    const strategy = resolutionByDraftId[draft.id]
    if (!strategy) return true
    if (strategy !== 'keepEnd') return false
    return !resolveDraftDates(draft.config.campaign, undefined, now, business.timezone)
      .keepEndAvailable
  })
  const hasUnresolvedStaleDrafts = unresolvedStaleDrafts.length > 0

  const noneSelectedBlocker =
    selectedDrafts.length > 0
      ? ''
      : draftCampaigns.length === 0
      ? 'No campaigns are ready to publish.'
      : 'Select at least one campaign.'
  const publishBlocker =
    noneSelectedBlocker ||
    (hasUnresolvedStaleDrafts
      ? `${
          unresolvedStaleDrafts.length === 1
            ? 'One campaign has'
            : `${unresolvedStaleDrafts.length} campaigns have`
        } dates that have already passed — set new dates above to publish.`
      : '')

  // Exact dates rather than "Started 3 days ago": a draft has never run, so relative tense
  // about its start reads as though it had.
  // The publish rules keep dateless drafts out of this drawer, but it is always mounted, so a
  // throw here blanks the whole tab (DRI-518). Degrade to text instead.
  const formatDraftDates = ({ startTime, endTime }: { startTime: number; endTime: number }) =>
    [startTime, endTime]
      .map(time =>
        isValidTime(time)
          ? format(utcToZonedTime(fromUnixTime(time), business.timezone), 'MMM d, yyyy')
          : 'No date'
      )
      .join(' – ')

  const isSelected = (draft: DraftCampaign) => draftSelection.some(({ id }) => id === draft.id)

  const clickDraft = (draft: DraftCampaign) =>
    setDraftSelection(selection =>
      selection.some(({ id }) => id === draft.id)
        ? selection.filter(({ id }) => id !== draft.id)
        : [...selection, { id: draft.id, name: draft.config.campaign.name }]
    )

  const settleResult = (draftId: number, settled: Omit<PublishResult, 'draftId' | 'name'>) =>
    setPublishResults(results =>
      results.map(result => (result.draftId === draftId ? { ...result, ...settled } : result))
    )

  const onComplete = async () => {
    setUploading(true)
    setPublishResults(
      selectedDrafts.map(draft => ({
        draftId: draft.id,
        name: draft.config.campaign.name,
        status: 'publishing' as const,
      }))
    )

    // A creative shared by several drafts may be published to Facebook only once, and a draft
    // publishing it can't see another doing the same (DRI-289). So a draft waits for every earlier
    // draft it shares a creative with; drafts sharing nothing still publish side by side.
    const creativeIDsOf = (draft: DraftCampaign) =>
      new Set((draft.config.creativeGroups ?? []).flat().filter((id): id is number => !!id))
    const publishes: Promise<void>[] = []

    await Promise.all(
      selectedDrafts.map((draft, index) => {
        const creativeIDs = creativeIDsOf(draft)
        const sharesCreativeWith = selectedDrafts
          .slice(0, index)
          .flatMap((earlier, earlierIndex) =>
            Array.from(creativeIDsOf(earlier)).some(id => creativeIDs.has(id))
              ? [publishes[earlierIndex]]
              : []
          )
        // Every publish settles its own result and never rejects, so this only waits.
        const publish = Promise.all(sharesCreativeWith).then(() => publishDraft(draft))
        publishes.push(publish)
        return publish
      })
    )

    setUploading(false)
    setUploadComplete(true)
    refetchPublishedCampaigns()
  }

  const publishDraft = (draft: DraftCampaign): Promise<void> => {
    // Resolved from the same helper the notice previewed, so what the AE saw is what ships.
    const { startTime, endTime } = resolveDraftDates(
      draft.config.campaign,
      resolutionByDraftId[draft.id],
      now,
      business.timezone
    )

    // Must return the promise — without it the drafts waiting on this one, and the "complete"
    // screen plus refetchPublishedCampaigns, go ahead before anything has published.
    return (
      campaignService
        // @ts-ignore
        .publishCampaign(Number(businessId), {
          ...draft.config,
          campaign: { ...draft.config.campaign, startTime, endTime },
          sprintID: props.sprintId,
          // after the spread so the AE's choice wins over anything stored in the draft's config
          publishPaused,
        })
        .then(res => {
          if (res) {
            settleResult(draft.id, {
              status: 'success',
              message: publishPaused ? 'Published, paused' : 'Published',
            })
            deleteDraftCampaign(draft.id)
            return
          }

          settleResult(draft.id, {
            status: 'error',
            message: 'Published with errors',
            description: 'The error is listed on the Published Campaigns tab.',
          })
        })
        .catch(err => {
          const isTimeout = err.code === 'ECONNABORTED' || err.response?.status === 504
          // Only errors Facebook returned are tagged as Facebook's; our own checks answer in the
          // same shape without the tag, and must not send the AE to Facebook to fix them.
          const fromFacebook = err.response?.data?.source === 'facebook'
          settleResult(draft.id, {
            status: isTimeout ? 'warning' : 'error',
            message: isTimeout
              ? 'Request timed out'
              : fromFacebook
              ? 'Facebook error'
              : "Couldn't publish",
            description: isTimeout
              ? 'The publish is likely still processing. Check the Published Campaigns tab shortly.'
              : `${err?.response?.data?.message ?? err.message}`,
          })
        })
    )
  }

  const closeResults = () => {
    toast.info(
      'Published campaigns can take a few moments to appear on the Published Campaigns tab.'
    )
    props.onClose()
  }

  const publishedCount = publishResults.filter(result => result.status === 'success').length
  const needsAttentionCount = publishResults.filter(result => result.status === 'error').length
  const unconfirmedCount = publishResults.filter(result => result.status === 'warning').length
  const finishedCount = publishResults.filter(result => result.status !== 'publishing').length

  return (
    <Drawer
      anchor="right"
      open={props.isOpen}
      onClose={uploading ? undefined : uploadComplete ? closeResults : props.onClose}
      disableEscapeKeyDown={uploading}
    >
      <div style={{ width: '90vw' }} className="flex h-full max-w-full flex-col overflow-hidden">
        {!(uploadComplete || uploading) && (
          <Stepper submitButtonText="Publish Campaigns">
            <Step
              title={'Choose Campaigns'}
              completed={selectedDrafts.length > 0}
              canContinue={selectedDrafts.length > 0}
              disabled={!!noneSelectedBlocker}
              disabledReason={noneSelectedBlocker}
            >
              <BlankPageLayout
                title="Choose Campaigns to Publish"
                subtitle="Only the campaigns you select will be published to Facebook."
              >
                {draftCampaigns.length > 0 ? (
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={selectedDrafts.length === draftCampaigns.length}
                          indeterminate={
                            selectedDrafts.length > 0 &&
                            selectedDrafts.length < draftCampaigns.length
                          }
                          onChange={(_, checked) =>
                            setDraftSelection(
                              checked
                                ? draftCampaigns.map(draft => ({
                                    id: draft.id,
                                    name: draft.config.campaign.name,
                                  }))
                                : []
                            )
                          }
                        />
                      }
                      label="Select all campaigns"
                    />
                    <span className="text-sm text-mm-neutral-1-600" role="status">
                      {selectedDrafts.length} of {draftCampaigns.length} selected
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-mm-neutral-1-600">
                    No campaigns are ready to publish. Close this panel and check your draft
                    campaigns for errors.
                  </p>
                )}
                <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {draftCampaigns.map(draftCampaign => (
                    <label
                      key={draftCampaign.id}
                      className={`flex items-center gap-2 w-full p-4 border rounded-md cursor-pointer focus-within:ring-2 focus-within:ring-mm-primary-500 ${
                        isSelected(draftCampaign)
                          ? 'border-mm-primary-500 bg-mm-primary-50'
                          : 'border-mm-neutral-1-200 hover:bg-mm-neutral-1-50'
                      }`}
                    >
                      <Checkbox
                        color="primary"
                        onChange={() => {
                          clickDraft(draftCampaign)
                        }}
                        checked={isSelected(draftCampaign)}
                      />
                      <span className="min-w-0 break-words">
                        <span className="block font-semibold">
                          {draftCampaign.config.campaign.name}
                        </span>
                        <span className="block text-sm text-mm-neutral-1-600">
                          {formatDraftDates(draftCampaign.config.campaign)}
                          {resolveDraftDates(
                            draftCampaign.config.campaign,
                            undefined,
                            now,
                            business.timezone
                          ).stale && (
                            <span className="font-medium text-mm-warning-800">
                              {' '}
                              · start date passed
                            </span>
                          )}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </BlankPageLayout>
            </Step>
            <Step
              title={'Review & Publish'}
              onNext={onComplete}
              disabled={!!publishBlocker}
              disabledReason={publishBlocker}
            >
              <BlankPageLayout
                title="Review Campaigns"
                subtitle="These campaigns will be published to Facebook. Check each one before you publish."
              >
                <div className="w-full flex flex-col p-4 space-y-4">
                  {business.errors?.includes(BusinessError.fb_system_user_mismatch) && (
                    <Alert severity="warning">
                      <strong>{ADS_PUBLISH_WARNING_TITLE}</strong> {ADS_PUBLISH_WARNING_MESSAGE}{' '}
                      <AdminFixLink businessID={business.id} />
                    </Alert>
                  )}
                  {withdrawnDrafts.length > 0 && (
                    <Alert severity="warning">
                      {withdrawnDrafts.map(draft => draft.name).join(', ')} can no longer be
                      published — fix the errors listed on the draft campaign, then return here.
                    </Alert>
                  )}

                  <StaleDraftDatesNotice
                    staleDrafts={staleSelectedDrafts}
                    resolutionByDraftId={resolutionByDraftId}
                    setResolution={(draftId, strategy) =>
                      setResolutionByDraftId(prev => ({ ...prev, [draftId]: strategy }))
                    }
                    applyToAll={strategy =>
                      setResolutionByDraftId(prev => ({
                        ...prev,
                        ...Object.fromEntries(staleSelectedDrafts.map(d => [d.id, strategy])),
                      }))
                    }
                    now={now}
                    timezone={business.timezone}
                  />

                  {/* Boxed, and tinted when on, because this is the control that decides whether
                      money starts moving — bare in a stack it read as another line of text. */}
                  <FormControlLabel
                    className={`m-0 max-w-2xl items-start self-start rounded-md border py-2 pl-2 pr-4 transition-colors ${
                      publishPaused
                        ? 'border-mm-primary-600 bg-mm-primary-50'
                        : 'border-mm-neutral-1-200 bg-white'
                    }`}
                    control={
                      <Checkbox
                        color="primary"
                        checked={publishPaused}
                        onChange={(_, checked) => setPublishPaused(checked)}
                      />
                    }
                    label={
                      <span className="block py-1 text-sm text-mm-neutral-1-700">
                        <span className="block font-semibold text-mm-neutral-1-900">
                          Publish paused
                        </span>
                        Campaigns can start spending on their scheduled start date unless you tick
                        this. Paused campaigns spend nothing until you start them in Ads Manager.
                      </span>
                    }
                  />

                  <div className="space-x-4">
                    <Tabs
                      value={currentViewedDraft?.id ?? false}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      aria-label="Selected campaigns to review"
                    >
                      {selectedDrafts.map(draftCampaign => (
                        <Tab
                          key={draftCampaign.id}
                          style={{
                            paddingLeft: '10px',
                          }}
                          value={draftCampaign.id}
                          label={draftCampaign.config.campaign.name}
                          onClick={() => setCurrentViewedDraftId(draftCampaign.id)}
                        />
                      ))}
                    </Tabs>
                  </div>

                  {currentViewedDraft && previewDates ? (
                    <CampaignDetails
                      campaignDto={{
                        ...currentViewedDraft.config,
                        campaign: {
                          ...currentViewedDraft.config.campaign,
                          startTime: previewDates.startTime,
                          endTime: previewDates.endTime,
                        },
                      }}
                      selectedCreatives={getAdCreativeGroups(
                        currentViewedDraft.config.creativeGroups,
                        props?.creatives || []
                      )}
                      selectedAudiences={props?.audiences?.filter(a =>
                        currentViewedDraft.config.audienceIDs.includes(a.id)
                      )}
                    />
                  ) : (
                    <p>Select a draft to view</p>
                  )}
                </div>
              </BlankPageLayout>
            </Step>
          </Stepper>
        )}

        {(uploadComplete || uploading) && (
          <>
            {/* The header names the campaign work and carries the close, the results scroll
                between, and the action sits in a bar that stays put — the same frame as the
                draft-campaign editing drawer this opens beside. */}
            <div className="flex items-center gap-3 border-b border-mm-neutral-1-200 bg-white px-6 py-2">
              <h2 className="min-w-0 flex-1 truncate text-base font-bold text-mm-neutral-1-900">
                {uploading
                  ? `Publishing ${publishResults.length} ${
                      publishResults.length === 1 ? 'campaign' : 'campaigns'
                    }`
                  : `${publishedCount} of ${publishResults.length} ${
                      publishResults.length === 1 ? 'campaign' : 'campaigns'
                    } published`}
              </h2>
              {!uploading && (
                <IconButton onClick={closeResults} aria-label="Close" size="small">
                  <CloseIcon />
                </IconButton>
              )}
            </div>

            <div className="flex-grow overflow-y-auto">
              <BlankPageLayout
                subtitle={
                  uploading
                    ? 'Leave this panel open until every campaign has finished.'
                    : unconfirmedCount > 0
                    ? 'Some results are still unconfirmed. Check Published Campaigns before trying again to avoid publishing duplicates.'
                    : needsAttentionCount > 0
                    ? 'Some campaigns need attention. Review the results below and check Published Campaigns before trying again.'
                    : 'They may take a few moments to appear on the Published Campaigns tab.'
                }
              >
                <div className="space-y-2">
                  {publishResults.map(result => (
                    <Alert
                      key={result.draftId}
                      severity={result.status === 'publishing' ? 'info' : result.status}
                      variant="standard"
                      icon={
                        result.status === 'publishing' ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : undefined
                      }
                    >
                      <span className="font-semibold">{result.name}</span>
                      {result.status === 'publishing' ? (
                        <span className="text-mm-neutral-1-600"> — publishing…</span>
                      ) : (
                        <span> — {result.message}</span>
                      )}
                      {result.description && (
                        <span className="block text-sm">{result.description}</span>
                      )}
                    </Alert>
                  ))}
                </div>
              </BlankPageLayout>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-mm-neutral-1-200 bg-white px-6 py-3">
              <span className="mr-auto text-sm text-mm-neutral-1-600" role="status">
                {uploading
                  ? `${finishedCount} of ${publishResults.length} finished`
                  : [
                      needsAttentionCount > 0 && `${needsAttentionCount} need attention`,
                      unconfirmedCount > 0 && `${unconfirmedCount} unconfirmed`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
              </span>
              <Button
                variant="contained"
                color="primary"
                disabled={uploading}
                onClick={closeResults}
              >
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </Drawer>
  )
}

type PublishResult = {
  draftId: number
  name: string
  status: 'publishing' | 'error' | 'success' | 'warning'
  message?: string
  description?: string
}
